import {CONSTANTS, Mesh} from '../../index.js';

/**
 * Creates Hypercube.js icosahedron meshes.
 * @example
 * const meshicosahedron = new MeshIcosahedron(name);
 */
class MeshIcosahedron extends Mesh {

    /**
     * Stores the golden ratio.
     * @type {number}
     * @protected
     */
    $phi = CONSTANTS.GOLDEN_RATIO;

    /**
     * Stores the inverse of the golden ratio.
     * @type {number}
     * @protected
     */
    $phiinverse = this.$phi - 1;

    /**
     * Stores the indices.
     * @type {number[]}
     * @protected
     */
    $indices = [

        // -x faces
        0, 1, 2,

        3, 4, 5,

        // +x faces
        6, 7, 8,

        9, 10, 11,

        // -y faces
        12, 13, 14,

        15, 16, 17,

        // +y faces
        18, 19, 20,

        21, 22, 23,

        // -z faces
        24, 25, 26,

        27, 28, 29,

        // +z faces
        30, 31, 32,

        33, 34, 35,

        // -x/-y/-z face
        36, 37, 38,

        // +x/-y/-z face
        39, 40, 41,

        // -x/+y/-z face
        42, 43, 44,

        // +x/+y/-z face
        45, 46, 47,

        // -x/-y/+z face
        48, 49, 50,

        // +x/-y/+z face
        51, 52, 53,

        // -x/+y/+z face
        54, 55, 56,

        // +x/+y/+z face
        57, 58, 59
    ];

    /**
     * Stores the normals.
     * @type {number[]}
     * @protected
     */
    $normals = [

        // -x faces
        -(this.$phi + 1), 0, -1,
        -(this.$phi + 1), 0, -1,
        -(this.$phi + 1), 0, -1,

        -(this.$phi + 1), 0, 1,
        -(this.$phi + 1), 0, 1,
        -(this.$phi + 1), 0, 1,

        // +x faces
        this.$phi + 1, 0, -1,
        this.$phi + 1, 0, -1,
        this.$phi + 1, 0, -1,

        this.$phi + 1, 0, 1,
        this.$phi + 1, 0, 1,
        this.$phi + 1, 0, 1,

        // -y faces
        -1, -(this.$phi + 1), 0,
        -1, -(this.$phi + 1), 0,
        -1, -(this.$phi + 1), 0,

        1, -(this.$phi + 1), 0,
        1, -(this.$phi + 1), 0,
        1, -(this.$phi + 1), 0,

        // +y faces
        -1, this.$phi + 1, 0,
        -1, this.$phi + 1, 0,
        -1, this.$phi + 1, 0,

        1, this.$phi + 1, 0,
        1, this.$phi + 1, 0,
        1, this.$phi + 1, 0,

        // -z faces
        0, -1, -(this.$phi + 1),
        0, -1, -(this.$phi + 1),
        0, -1, -(this.$phi + 1),

        0, 1, -(this.$phi + 1),
        0, 1, -(this.$phi + 1),
        0, 1, -(this.$phi + 1),

        // +z faces
        0, -1, this.$phi + 1,
        0, -1, this.$phi + 1,
        0, -1, this.$phi + 1,

        0, 1, this.$phi + 1,
        0, 1, this.$phi + 1,
        0, 1, this.$phi + 1,

        // -x/-y/-z face
        -(this.$phi), -(this.$phi), -(this.$phi),
        -(this.$phi), -(this.$phi), -(this.$phi),
        -(this.$phi), -(this.$phi), -(this.$phi),

        // +x/-y/-z face
        this.$phi, -(this.$phi), -(this.$phi),
        this.$phi, -(this.$phi), -(this.$phi),
        this.$phi, -(this.$phi), -(this.$phi),

        // -x/+y/-z face
        -(this.$phi), this.$phi, -(this.$phi),
        -(this.$phi), this.$phi, -(this.$phi),
        -(this.$phi), this.$phi, -(this.$phi),

        // +x/+y/-z face
        this.$phi, this.$phi, -(this.$phi),
        this.$phi, this.$phi, -(this.$phi),
        this.$phi, this.$phi, -(this.$phi),

        // -x/-y/+z face
        -(this.$phi), -(this.$phi), this.$phi,
        -(this.$phi), -(this.$phi), this.$phi,
        -(this.$phi), -(this.$phi), this.$phi,

        // +x/-y/+z face
        this.$phi, -(this.$phi), this.$phi,
        this.$phi, -(this.$phi), this.$phi,
        this.$phi, -(this.$phi), this.$phi,

        // -x/+y/+z face
        -(this.$phi), this.$phi, this.$phi,
        -(this.$phi), this.$phi, this.$phi,
        -(this.$phi), this.$phi, this.$phi,

        // +x/+y/+z face
        this.$phi, this.$phi, this.$phi,
        this.$phi, this.$phi, this.$phi,
        this.$phi, this.$phi, this.$phi
    ];

    /**
     * Stores the positions.
     * @type {number[]}
     * @protected
     */
    $positions = [

        // -x faces
        -1, -(this.$phiinverse), 0,
        -(this.$phiinverse), 0, -1,
        -1, this.$phiinverse, 0,

        -1, -(this.$phiinverse), 0,
        -1, this.$phiinverse, 0,
        -(this.$phiinverse), 0, 1,

        // +x faces
        1, -(this.$phiinverse), 0,
        1, this.$phiinverse, 0,
        this.$phiinverse, 0, -1,

        1, -(this.$phiinverse), 0,
        this.$phiinverse, 0, 1,
        1, this.$phiinverse, 0,

        // -y faces
        0, -1, -(this.$phiinverse),
        -1, -(this.$phiinverse), 0,
        0, -1, this.$phiinverse,

        0, -1, -(this.$phiinverse),
        0, -1, this.$phiinverse,
        1, -(this.$phiinverse), 0,

        // +y faces
        0, 1, -(this.$phiinverse),
        0, 1, this.$phiinverse,
        -1, this.$phiinverse, 0,

        0, 1, -(this.$phiinverse),
        1, this.$phiinverse, 0,
        0, 1, this.$phiinverse,

        // -z faces
        -(this.$phiinverse), 0, -1,
        0, -1, -(this.$phiinverse),
        this.$phiinverse, 0, -1,

        -(this.$phiinverse), 0, -1,
        this.$phiinverse, 0, -1,
        0, 1, -(this.$phiinverse),

        // +z faces
        -(this.$phiinverse), 0, 1,
        this.$phiinverse, 0, 1,
        0, -1, this.$phiinverse,

        -(this.$phiinverse), 0, 1,
        0, 1, this.$phiinverse,
        this.$phiinverse, 0, 1,

        // -x/-y/-z face
        -1, -(this.$phiinverse), 0,
        0, -1, -(this.$phiinverse),
        -(this.$phiinverse), 0, -1,

        // +x/-y/-z face
        1, -(this.$phiinverse), 0,
        this.$phiinverse, 0, -1,
        0, -1, -(this.$phiinverse),

        // -x/+y/-z face
        -1, this.$phiinverse, 0,
        -(this.$phiinverse), 0, -1,
        0, 1, -(this.$phiinverse),

        // +x/+y/-z face
        1, this.$phiinverse, 0,
        0, 1, -(this.$phiinverse),
        this.$phiinverse, 0, -1,

        // -x/-y/+z face
        -1, -(this.$phiinverse), 0,
        -(this.$phiinverse), 0, 1,
        0, -1, this.$phiinverse,

        // +x/-y/+z face
        1, -(this.$phiinverse), 0,
        0, -1, this.$phiinverse,
        this.$phiinverse, 0, 1,

        // -x/+y/+z face
        -1, this.$phiinverse, 0,
        0, 1, this.$phiinverse,
        -(this.$phiinverse), 0, 1,

        // +x/+y/+z face
        1, this.$phiinverse, 0,
        this.$phiinverse, 0, 1,
        0, 1, this.$phiinverse
    ];

    /**
     * Stores the type.
     * @type {string}
     * @protected
     */
    $type = 'mesh.icosahedron';

    /**
     * Creates a new Hypercube.js icosahedron mesh.
     * @param {string} $name The name of the mesh to create.
     */
    constructor($name) {

        super($name);
    }
}

export {

    MeshIcosahedron
};

export default MeshIcosahedron;
